import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { environment } from '../environment/environment';
import { AUTH_ROUTES, SHARED_ENV } from '@trading-fe/shared';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { IconDefinition } from '@ant-design/icons-angular';
// Register only the icons actually used: those referenced in app templates
// plus the ones ng-zorro components use internally (date-picker, select,
// pagination, table, modal, notification, etc.). Importing the full icon set
// (~830 icons) pulled ~680 KB of unused SVGs into the main bundle.
import {
  AlertFill, AlertOutline, AlertTwoTone,
  AndroidOutline, AppleOutline,
  BankFill, BankOutline, BankTwoTone,
  BarsOutline, BorderOutline,
  CaretDownFill, CaretDownOutline, CaretUpFill, CaretUpOutline,
  CheckCircleFill, CheckCircleOutline, CheckCircleTwoTone, CheckOutline, CheckSquareOutline,
  ClearOutline, ClockCircleOutline,
  CloseCircleFill, CloseCircleOutline, CloseCircleTwoTone, CloseOutline,
  CopyFill, CopyOutline, CopyTwoTone,
  DeleteFill, DeleteOutline, DeleteTwoTone,
  DiffFill, DiffOutline, DiffTwoTone,
  DisconnectOutline,
  DollarOutline, DollarTwoTone,
  DoubleLeftOutline, DoubleRightOutline,
  DownOutline, DownloadOutline,
  EditFill, EditOutline, EditTwoTone,
  EllipsisOutline, EnterOutline,
  ExclamationCircleFill, ExclamationCircleOutline, ExclamationCircleTwoTone,
  ExpandOutline, ExportOutline,
  EyeFill, EyeOutline, EyeTwoTone,
  FileFill, FileOutline, FileTextOutline, FileTwoTone,
  FilterFill, FilterOutline, FilterTwoTone,
  FunctionOutline,
  GiftFill, GiftOutline, GiftTwoTone,
  HistoryOutline,
  ImportOutline,
  InfoCircleFill, InfoCircleOutline, InfoCircleTwoTone,
  LeftOutline, LinkOutline, LoadingOutline,
  MailOutline, PhoneOutline, CalendarOutline,
  MenuOutline, NotificationOutline, NumberOutline,
  PaperClipOutline, PictureOutline,
  PlayCircleFill, PlayCircleOutline, PlayCircleTwoTone,
  PlusCircleFill, PlusCircleOutline, PlusCircleTwoTone, PlusOutline,
  ReloadOutline, RetweetOutline, RightOutline,
  SafetyCertificateFill, SafetyCertificateOutline, SafetyCertificateTwoTone,
  SearchOutline, SelectOutline, SendOutline, StockOutline,
  StopFill, StopOutline, StopTwoTone,
  SwapOutline, SwapRightOutline, SyncOutline,
  TableOutline, TeamOutline,
  ToolFill, ToolOutline, ToolTwoTone,
  TranslationOutline,
  UnlockOutline,
  UpOutline, UploadOutline, UserOutline,
  WarningFill, WarningOutline, WarningTwoTone,
} from '@ant-design/icons-angular/icons';
import { tokenInterceptor } from '@trading-fe/interceptors';
import {
  CurrencyCacheService,
  InstrumentPrecisionService,
} from '@trading-fe/services';
import { provideNzI18n, en_US } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
registerLocaleData(en);

const icons: IconDefinition[] = [
  AlertFill, AlertOutline, AlertTwoTone,
  AndroidOutline, AppleOutline,
  BankFill, BankOutline, BankTwoTone,
  BarsOutline, BorderOutline,
  CaretDownFill, CaretDownOutline, CaretUpFill, CaretUpOutline,
  CheckCircleFill, CheckCircleOutline, CheckCircleTwoTone, CheckOutline, CheckSquareOutline,
  ClearOutline, ClockCircleOutline,
  CloseCircleFill, CloseCircleOutline, CloseCircleTwoTone, CloseOutline,
  CopyFill, CopyOutline, CopyTwoTone,
  DeleteFill, DeleteOutline, DeleteTwoTone,
  DiffFill, DiffOutline, DiffTwoTone,
  DisconnectOutline,
  DollarOutline, DollarTwoTone,
  DoubleLeftOutline, DoubleRightOutline,
  DownOutline, DownloadOutline,
  EditFill, EditOutline, EditTwoTone,
  EllipsisOutline, EnterOutline,
  ExclamationCircleFill, ExclamationCircleOutline, ExclamationCircleTwoTone,
  ExpandOutline, ExportOutline,
  EyeFill, EyeOutline, EyeTwoTone,
  FileFill, FileOutline, FileTextOutline, FileTwoTone,
  FilterFill, FilterOutline, FilterTwoTone,
  FunctionOutline,
  GiftFill, GiftOutline, GiftTwoTone,
  HistoryOutline,
  ImportOutline,
  InfoCircleFill, InfoCircleOutline, InfoCircleTwoTone,
  LeftOutline, LinkOutline, LoadingOutline,
  MailOutline, PhoneOutline, CalendarOutline,
  MenuOutline, NotificationOutline, NumberOutline,
  PaperClipOutline, PictureOutline,
  PlayCircleFill, PlayCircleOutline, PlayCircleTwoTone,
  PlusCircleFill, PlusCircleOutline, PlusCircleTwoTone, PlusOutline,
  ReloadOutline, RetweetOutline, RightOutline,
  SafetyCertificateFill, SafetyCertificateOutline, SafetyCertificateTwoTone,
  SearchOutline, SelectOutline, SendOutline, StockOutline,
  StopFill, StopOutline, StopTwoTone,
  SwapOutline, SwapRightOutline, SyncOutline,
  TableOutline, TeamOutline,
  ToolFill, ToolOutline, ToolTwoTone,
  TranslationOutline,
  UnlockOutline,
  UpOutline, UploadOutline, UserOutline,
  WarningFill, WarningOutline, WarningTwoTone,
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideNzI18n(en_US),
    provideNzIcons(icons),
    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withPreloading(PreloadAllModules)
    ),
    provideAnimations(),
    { provide: SHARED_ENV, useValue: environment },
    {
      provide: AUTH_ROUTES,
      useValue: { loginUrl: '/', homeUrl: '/admin', tfaUrl: '/tfa' },
    },
    provideAppInitializer(() => inject(CurrencyCacheService).loadCurrencies()),
    provideAppInitializer(() =>
      inject(InstrumentPrecisionService).loadPrecisions()
    ),
  ],
};
